import ApartmentService from '../../services/ApartmentService'
import BuildingService from '../../services/BuildingService'
import AxiosErrorHandler from '../../helpers/AxiosErrorHandler'
import router from '../../router'
import ProjectsService from '../../services/ProjectsService'
const enums = require('../../helpers/Enums')

// Maps a statusId to the string form apartment-service.js's create() needs
// (see copyApartments below) — mirrors the setApartmentValue action's mapping.
function statusIdToValue(statusId) {
  const apartmentStatus = enums.default.ApartmentStatus
  if (statusId === apartmentStatus.apartment_status_available)
    return 'available'
  if (statusId === apartmentStatus.apartment_status_reserved) return 'reserved'
  if (statusId === apartmentStatus.apartment_status_sold) return 'sold'
  if (statusId === apartmentStatus.apartment_status_unavailable)
    return 'unavailable'
  return undefined
}

export const namespaced = true

const initialState = {
  apartments: [],
  projectApartments: [],
  selectedApartment: null,
  status: null
}

export const state = {
  apartments: [],
  projectApartments: [],
  selectedApartment: null,
  status: null
}

export const mutations = {
  SET_APARTMENTS(state, recs) {
    state.apartments = recs
  },
  SET_PROJECT_APARTMENTS(state, recs) {
    state.projectApartments = recs
  },
  SET_SELECTED_APARTMENT(state, rec) {
    state.selectedApartment = rec
  },
  SET_APARTMENT_VALUE(state, status) {
    state.status = status
  },
  RESET_STATUS_VALUE(state) {
    state.status = null
  },
  RESET_STORE(state) {
    Object.assign(state, initialState)
  }
}

export const actions = {
  fetchApartments: function(context, filters = {}) {
    context.commit('SET_LOADING_STATUS', 'loading', { root: true })
    return BuildingService.getApartmentsForBuilding(
      context.rootGetters['BuildingStore/getSelectedBuildingId'],
      filters
    ).then(
      r => {
        context.commit('SET_APARTMENTS', r.data)
        context.commit('SET_LOADING_STATUS', 'idle', { root: true })
      },
      e => {
        context.commit('SET_APARTMENTS', null)
        context.commit('SET_LOADING_STATUS', 'idle', { root: true })
        AxiosErrorHandler.handler(e, context, router)
      }
    )
  },
  fetchProjectApartments: function(context, filters = {}) {
    context.commit('SET_LOADING_STATUS', 'loading', { root: true })
    return ProjectsService.getProjectApartments(
      context.rootGetters['ProjectStore/getSelectedProjectId'],
      filters
    ).then(
      r => {
        context.commit('SET_PROJECT_APARTMENTS', r.data)
        context.commit('SET_LOADING_STATUS', 'idle', { root: true })
      },
      e => {
        context.commit('SET_PROJECT_APARTMENTS', null)
        context.commit('SET_LOADING_STATUS', 'idle', { root: true })
        AxiosErrorHandler.handler(e, context, router)
      }
    )
  },
  setSelectedApartment: function(context, value) {
    context.commit('SET_LOADING_STATUS', 'loading', { root: true })
    return ApartmentService.getApartmentById(value).then(
      r => {
        context.commit('SET_SELECTED_APARTMENT', r.data)
        context.dispatch(
          'BreadcrumbsStore/add',
          {
            text: 'APARTMENT ',
            name: r.data.name
          },
          { root: true }
        )
        context.commit('SET_LOADING_STATUS', 'idle', { root: true })
      },
      e => {
        context.commit('SET_SELECTED_APARTMENT', null)
        context.commit('SET_LOADING_STATUS', 'idle', { root: true })
        AxiosErrorHandler.handler(e, context, router)
      }
    )
  },
  addApartment: function(context, value) {
    context.commit('SET_LOADING_STATUS', 'loading', { root: true })

    return ApartmentService.addApartment(value).then(
      context.dispatch('fetchApartments')
    )
  },
  updateApartment: function(context, value) {
    context.commit('SET_LOADING_STATUS', 'loading', { root: true })

    return ApartmentService.updateApartment(value).then(
      res => {
        context.commit('SET_SELECTED_APARTMENT', res.data)
        context.dispatch(
          'BreadcrumbsStore/update',
          {
            text: 'APARTMENT ',
            name: res.data.name
          },
          { root: true }
        )
        context.commit('SET_LOADING_STATUS', 'idle', { root: true })
        context.dispatch('fetchProjectApartments', {
          root: true
        })
        context.dispatch(
          'NotificationStore/add',
          { type: 'info', message: 'Apartment data successfully updated' },
          { root: true }
        )
      },
      e => {
        context.commit('SET_LOADING_STATUS', 'idle', { root: true })
        AxiosErrorHandler.handler(e, context, router)
      }
    )
  },
  // Duplicates each given apartment `copies` times. Each duplicate drops the
  // original's id (so it's created as a new record) and externalId (reserved
  // for CRM sync via th-queue — a copy is not the same real-world unit), and
  // gets a "<name> copy (<n>)" suffix per copy number. Batches all creations
  // then refetches once, rather than relying on addApartment's per-call
  // refetch.
  copyApartments: async function(context, { apartments, copies }) {
    context.commit('SET_LOADING_STATUS', 'loading', { root: true })
    try {
      const creations = []
      apartments.forEach(apartment => {
        for (let i = 1; i <= copies; i++) {
          const clone = Object.assign({}, apartment)
          delete clone.id
          clone.externalId = ''
          clone.name = `${apartment.name} copy (${i})`
          // apartment-service.js's create() does
          // `data.properties.status = data.statusValue` unconditionally —
          // without this, every copy's stored properties.status silently
          // becomes undefined (statusId itself would still be correct, but
          // the string label wouldn't be), since statusValue is a write-only
          // field the regular "New Apartment" form sets and a fetched
          // apartment record never carries.
          clone.statusValue = statusIdToValue(apartment.statusId)
          creations.push(ApartmentService.addApartment(clone))
        }
      })
      await Promise.all(creations)
      await context.dispatch('fetchApartments')
      context.commit('SET_LOADING_STATUS', 'idle', { root: true })
      context.dispatch(
        'NotificationStore/add',
        {
          type: 'info',
          message: `Created ${creations.length} apartment ${
            creations.length === 1 ? 'copy' : 'copies'
          }`
        },
        { root: true }
      )
    } catch (error) {
      context.commit('SET_LOADING_STATUS', 'idle', { root: true })
      AxiosErrorHandler.handler(error, context, router)
      throw error
    }
  },
  deleteApartment: async function(context, value) {
    context.commit('SET_LOADING_STATUS', 'loading', { root: true })
    try {
      return await ApartmentService.deleteApartment(value)
    } catch (error) {
      context.commit('SET_LOADING_STATUS', 'idle', { root: true })
      AxiosErrorHandler.handler(error, context, router)
      throw error
    }
  },
  setApartmentValue: function(context, value) {
    const apartmentStatus = enums.default.ApartmentStatus
    if (value === apartmentStatus.apartment_status_available) {
      value = 'available'
    }
    if (value === apartmentStatus.apartment_status_reserved) {
      value = 'reserved'
    }
    if (value === apartmentStatus.apartment_status_sold) {
      value = 'sold'
    }
    if (value === apartmentStatus.apartment_status_unavailable) {
      value = 'unavailable'
    }
    context.commit('SET_APARTMENT_VALUE', value)
  },
  resetStore: async function(context) {
    context.commit('SET_LOADING_STATUS', 'loading', { root: true })
    context.commit('RESET_STORE')
    context.commit('SET_LOADING_STATUS', 'idle', { root: true })
  },
  resetStatusValue: function(context) {
    context.commit('RESET_STATUS_VALUE')
  }
}

export const getters = {
  getSelectedApartmentId: state => {
    return state.selectedApartment ? state.selectedApartment.id : 0
  }
}
